import { createSlice } from '@reduxjs/toolkit';
import { createAsyncThunkWithLoggingAndRetry } from '../../../store/middleware/thunk_helper';
import {
  getSchedulerNotificationMessages as getSchedulerNotificationMessagesService,
  saveSchedulerNotificationMessage as saveSchedulerNotificationMessageService,
  deleteSchedulerNotificationMessage as deleteSchedulerNotificationMessageService,
  getSchedulerAppointmentWindows as getSchedulerAppointmentWindowsService,
  saveSchedulerAppointmentWindow as saveSchedulerAppointmentWindowService,
  deleteSchedulerAppointmentWindow as deleteSchedulerAppointmentWindowService,
} from '../../../services/schedulerService';
import { FAILED, PENDING, REQUESTING, SUCCESS } from '../../../actions/statusTypes';

export const getSchedulerNotificationMessages = createAsyncThunkWithLoggingAndRetry(
  'airforceSetup/getSchedulerNotificationMessages',
  ({ employerId }) => {
    return getSchedulerNotificationMessagesService(employerId);
  }
);

export const getSchedulerAppointmentWindows = createAsyncThunkWithLoggingAndRetry(
  'airforceSetup/getSchedulerAppointmentWindows',
  ({ employerId }) => {
    return getSchedulerAppointmentWindowsService(employerId);
  }
);

const _saveSchedulerNotificationMessage = createAsyncThunkWithLoggingAndRetry(
  'airforceSetup/saveSchedulerNotificationMessage',
  ({ employerId, message }) => {
    return saveSchedulerNotificationMessageService(employerId, message);
  }
);

const _deleteSchedulerNotificationMessage = createAsyncThunkWithLoggingAndRetry(
  'airforceSetup/deleteSchedulerNotificationMessage',
  ({ employerId, schedulerMessageId }) => {
    return deleteSchedulerNotificationMessageService(employerId, schedulerMessageId);
  }
);

const _saveSchedulerAppointmentWindow = createAsyncThunkWithLoggingAndRetry(
  'airforceSetup/saveSchedulerAppointmentWindow',
  ({ employerId, appointmentWindow }) => {
    return saveSchedulerAppointmentWindowService(employerId, appointmentWindow);
  }
);

const _deleteSchedulerAppointmentWindow = createAsyncThunkWithLoggingAndRetry(
  'airforceSetup/deleteSchedulerAppointmentWindow',
  ({ employerId, employerAppointmentWindowsId }) => {
    return deleteSchedulerAppointmentWindowService(employerId, employerAppointmentWindowsId);
  }
);

export const saveSchedulerNotificationMessage = (params) => {
  return (dispatch) => {
    return dispatch(_saveSchedulerNotificationMessage(params));
  };
};

export const deleteSchedulerNotificationMessage = (params) => {
  return (dispatch) => {
    return dispatch(_deleteSchedulerNotificationMessage(params));
  };
};

export const saveSchedulerAppointmentWindow = (params) => {
  return async (dispatch) => {
    return dispatch(_saveSchedulerAppointmentWindow(params));
  };
};

export const deleteSchedulerAppointmentWindow = (params) => {
  return async (dispatch) => {
    return dispatch(_deleteSchedulerAppointmentWindow(params));
  };
};

const INITIAL_STATE = {
  notificationMessagesStatus: PENDING,
  notificationMessagesError: null,
  notificationMessagesData: [],
  saveNotificationMessageStatus: PENDING,
  saveNotificationMessageError: null,
  deleteNotificationMessageStatus: PENDING,
  deleteNotificationMessageError: null,
  appointmentWindowsStatus: PENDING,
  appointmentWindowsError: null,
  appointmentWindowsData: [],
  saveAppointmentWindowStatus: PENDING,
  saveAppointmentWindowError: null,
  deleteAppointmentWindowStatus: PENDING,
  deleteAppointmentWindowError: null,
};

const airforceSetupSlice = createSlice({
  name: 'airforceSetup',
  initialState: INITIAL_STATE,
  reducers: {
    resetState: () => {
      return {
        ...INITIAL_STATE,
      };
    },
    resetNotificationMessagesStatus: (state) => {
      return {
        ...state,
        notificationMessagesStatus: PENDING,
        notificationMessagesError: null,
        saveNotificationMessageStatus: PENDING,
        saveNotificationMessageError: null,
        deleteNotificationMessageStatus: PENDING,
        deleteNotificationMessageError: null,
      };
    },
    resetAppointmentWindowsState: (state) => {
      return {
        ...state,
        appointmentWindowsStatus: PENDING,
        appointmentWindowsError: null,
        appointmentWindowsData: [],
        saveAppointmentWindowStatus: PENDING,
        saveAppointmentWindowError: null,
        deleteAppointmentWindowStatus: PENDING,
        deleteAppointmentWindowError: null,
      };
    },
    resetAppointmentWindowsStatus: (state) => {
      return {
        ...state,
        appointmentWindowsStatus: PENDING,
        appointmentWindowsError: null,
        saveAppointmentWindowStatus: PENDING,
        saveAppointmentWindowError: null,
        deleteAppointmentWindowStatus: PENDING,
        deleteAppointmentWindowError: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSchedulerNotificationMessages.pending, (state) => {
        state.notificationMessagesStatus = REQUESTING;
        state.notificationMessagesError = null;
      })
      .addCase(getSchedulerNotificationMessages.fulfilled, (state, action) => {
        state.notificationMessagesStatus = SUCCESS;
        state.notificationMessagesData = action.payload || [];
      })
      .addCase(getSchedulerNotificationMessages.rejected, (state, action) => {
        state.notificationMessagesStatus = FAILED;
        state.notificationMessagesError = action.payload;
        state.notificationMessagesData = [];
      })
      .addCase(_saveSchedulerNotificationMessage.pending, (state) => {
        state.saveNotificationMessageStatus = REQUESTING;
        state.saveNotificationMessageError = null;
      })
      .addCase(_saveSchedulerNotificationMessage.fulfilled, (state) => {
        state.saveNotificationMessageStatus = SUCCESS;
      })
      .addCase(_saveSchedulerNotificationMessage.rejected, (state, action) => {
        state.saveNotificationMessageStatus = FAILED;
        state.saveNotificationMessageError = action.payload;
      })
      .addCase(_deleteSchedulerNotificationMessage.pending, (state) => {
        state.deleteNotificationMessageStatus = REQUESTING;
        state.deleteNotificationMessageError = null;
      })
      .addCase(_deleteSchedulerNotificationMessage.fulfilled, (state) => {
        state.deleteNotificationMessageStatus = SUCCESS;
      })
      .addCase(_deleteSchedulerNotificationMessage.rejected, (state, action) => {
        state.deleteNotificationMessageStatus = FAILED;
        state.deleteNotificationMessageError = action.payload;
      })
      .addCase(getSchedulerAppointmentWindows.pending, (state) => {
        state.appointmentWindowsStatus = REQUESTING;
        state.appointmentWindowsError = null;
      })
      .addCase(getSchedulerAppointmentWindows.fulfilled, (state, action) => {
        state.appointmentWindowsStatus = SUCCESS;
        state.appointmentWindowsData = action.payload || [];
      })
      .addCase(getSchedulerAppointmentWindows.rejected, (state, action) => {
        state.appointmentWindowsStatus = FAILED;
        state.appointmentWindowsError = action.payload;
        state.appointmentWindowsData = [];
      })
      .addCase(_saveSchedulerAppointmentWindow.pending, (state) => {
        state.saveAppointmentWindowStatus = REQUESTING;
        state.saveAppointmentWindowError = null;
      })
      .addCase(_saveSchedulerAppointmentWindow.fulfilled, (state) => {
        state.saveAppointmentWindowStatus = SUCCESS;
      })
      .addCase(_saveSchedulerAppointmentWindow.rejected, (state, action) => {
        state.saveAppointmentWindowStatus = FAILED;
        state.saveAppointmentWindowError = action.payload;
      })
      .addCase(_deleteSchedulerAppointmentWindow.pending, (state) => {
        state.deleteAppointmentWindowStatus = REQUESTING;
        state.deleteAppointmentWindowError = null;
      })
      .addCase(_deleteSchedulerAppointmentWindow.fulfilled, (state) => {
        state.deleteAppointmentWindowStatus = SUCCESS;
      })
      .addCase(_deleteSchedulerAppointmentWindow.rejected, (state, action) => {
        state.deleteAppointmentWindowStatus = FAILED;
        state.deleteAppointmentWindowError = action.payload;
      });
  },
});

export const {
  resetState,
  resetNotificationMessagesStatus,
  resetAppointmentWindowsState,
  resetAppointmentWindowsStatus,
} = airforceSetupSlice.actions;
export default airforceSetupSlice.reducer;
